import { z } from 'zod';
import { PLAIN_NAME_MESSAGE, plainName } from './json.js';
/**
 * Built-in sign-in (decision 0033): accounts with an email and a password, the email verified by a link,
 * and an optional second step (a code from an authenticator app). The API issues its own tokens, so an
 * external identity provider can still be added later without changing anything that reads them.
 */
/** The issuer of the API's own tokens. Fixed, so moving to another domain doesn't change who people are. */
export const ACCOUNTS_ISSUER = 'urn:cyberscore:accounts';
export const PASSWORD_MIN = 12;
export const PASSWORD_MAX = 128;
const Email = z
    .string()
    .trim()
    .toLowerCase()
    .pipe(z.email('Enter an email address'))
    .pipe(z.string().max(320, 'That email address is too long'));
const Name = z.string().trim().min(1, 'Enter your name').max(200, 'Use at most 200 characters').refine(plainName, PLAIN_NAME_MESSAGE); // prettier-ignore
// Length is checked here; the rest of the policy (passwordProblems) needs the email and name too.
const Password = z
    .string()
    .min(PASSWORD_MIN, `Use at least ${PASSWORD_MIN} characters`)
    .max(PASSWORD_MAX, `Use at most ${PASSWORD_MAX} characters`);
const Token = z.string().regex(/^[A-Za-z0-9_-]{43}$/, 'This link is not valid');
const Code = z
    .string()
    .trim()
    .transform((s) => s.replace(/\s+/g, ''))
    .pipe(z.string().regex(/^\d{6}$/, 'Enter the 6-digit code'));
const RecoveryCode = z
    .string()
    .trim()
    .toUpperCase()
    .transform((s) => s.replace(/[\s-]+/g, ''))
    .pipe(z.string().regex(/^[A-Z2-7]{10}$/, 'Enter a recovery code, like ABCD-EFGH-JK'));
export const SignUp = z.strictObject({ email: Email, name: Name, password: Password });
export const SignIn = z.strictObject({ email: Email, password: z.string().min(1, 'Enter your password').max(PASSWORD_MAX) }); // prettier-ignore
export const SecondStep = z.union([
    z.strictObject({ ticket: z.string().min(20).max(2000), code: Code }),
    z.strictObject({ ticket: z.string().min(20).max(2000), recovery_code: RecoveryCode }),
]);
export const EmailOnly = z.strictObject({ email: Email });
export const VerifyEmail = z.strictObject({ token: Token });
export const ResetPassword = z.strictObject({ token: Token, password: Password });
export const ChangePassword = z.strictObject({
    current: z.string().min(1).max(PASSWORD_MAX),
    password: Password,
});
export const UpdateAccount = z.strictObject({ name: Name });
export const EnableMfa = z.strictObject({ code: Code });
export const DisableMfa = z.strictObject({ password: z.string().min(1).max(PASSWORD_MAX), code: Code });
/**
 * Passwords people pick most, and words that make a password easy to guess here. A password containing
 * one of these is refused, whatever else it has.
 */
const COMMON = [
    'password', 'passw0rd', 'p@ssw0rd', 'p@ssword', '123456', '12345678', '1234567890', 'qwerty', 'qwertyuiop',
    'asdfgh', 'zxcvbn', 'abc123', 'letmein', 'welcome', 'iloveyou', 'admin', 'administrator', 'monkey',
    'dragon', 'sunshine', 'princess', 'football', 'baseball', 'starwars', 'trustno1', 'whatever', 'master',
    'shadow', 'superman', 'batman', 'changeme', 'secret', 'default', 'login', 'cyberscore', 'insurance',
    'malaysia', 'kualalumpur', 'selamat', 'sayang', 'abcdef', '111111', '000000', '987654321', 'qazwsx',
]; // prettier-ignore
/** What is wrong with a password, beyond its length, or nothing. */
export function passwordProblems(password, who = {}) {
    const problems = [];
    if (password.length < PASSWORD_MIN)
        problems.push(`Use at least ${PASSWORD_MIN} characters`);
    if (password.length > PASSWORD_MAX)
        problems.push(`Use at most ${PASSWORD_MAX} characters`);
    const lower = password.toLowerCase();
    if (COMMON.some((w) => lower.includes(w)))
        problems.push('Avoid common passwords and words like “password”');
    if (new Set(lower).size < 5)
        problems.push('Use more different characters');
    const local = who.email?.split('@')[0]?.toLowerCase() ?? '';
    const names = [local, ...(who.name ?? '').toLowerCase().split(/\s+/)].filter((w) => w.length >= 4);
    if (names.some((w) => lower.includes(w)))
        problems.push('Don’t use your name or email address');
    return problems;
}
/** Recovery codes are shown as ABCD-EFGH-JK. */
export const formatRecoveryCode = (c) => `${c.slice(0, 4)}-${c.slice(4, 8)}-${c.slice(8)}`;
//# sourceMappingURL=accounts.js.map